import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations } from '../translations/translations';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  tp: (key: string, params: Record<string, string | number>) => string;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    // Try to get the language from localStorage
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage === 'ar' ? 'ar' : 'en') as Language;
  });

  // Update document direction when language changes
  useEffect(() => {
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('language', language);

    // Add or remove RTL-specific styles
    if (language === 'ar') {
      document.body.classList.add('font-arabic');
    } else {
      document.body.classList.remove('font-arabic');
    }
  }, [language]);

  const isRTL = language === 'ar';

  const t = (key: string): string => {
    const parts = key.split('.');
    const langTranslations = language === 'ar' ? translations.ar : translations.en;

    let result: any = langTranslations;
    for (const part of parts) {
      if (result == null || typeof result !== 'object') return key;
      result = result[part];
    }

    return typeof result === 'string' ? result : key;
  };

  const tp = (key: string, params: Record<string, string | number>): string => {
    let text = t(key);

    for (const [paramKey, paramValue] of Object.entries(params)) {
      const placeholder = `{${paramKey}}`;
      text = text.replace(new RegExp(placeholder, 'g'), String(paramValue));
    }

    // Handle pluralization for count_plural placeholder
    if (params.count !== undefined) {
      const plural = params.count === 1 ? '' : 's';
      text = text.replace('{count_plural}', plural);
    }

    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, tp, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
